/**
 * Offline Context - Manages offline state, sync queue, and connection status
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  initOfflineDB,
  addPendingSale,
  addPendingOrder,
  updatePendingOrder,
  getPendingSales,
  getPendingOrders,
  getOfflineStats,
  cacheData,
  getCachedData,
} from '../services/offlineDB';
import {
  startAutoSync,
  stopAutoSync,
  forceSync,
  onSyncEvent,
  getSyncStatus,
} from '../services/syncService';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isDBReady, setIsDBReady] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncProgress, setSyncProgress] = useState(null);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  // Initialize offline database
  useEffect(() => {
    const init = async () => {
      try {
        await initOfflineDB();
        setIsDBReady(true);
        console.log('Offline database ready');
        
        // Load initial stats
        const stats = await getOfflineStats();
        setPendingCount(stats.totalPending);
        
        // Start auto-sync
        startAutoSync();
      } catch (error) {
        console.error('Failed to initialize offline database:', error);
      }
    };
    
    init();
    
    return () => {
      stopAutoSync();
    };
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Conexão restaurada', {
        description: 'Sincronizando dados pendentes...',
        duration: 3000,
      });
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Você está offline', {
        description: 'Os dados serão salvos localmente e sincronizados quando a conexão voltar.',
        duration: 5000,
      });
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for sync events
  useEffect(() => {
    const unsubscribe = onSyncEvent((event) => {
      switch (event.type) {
        case 'sync_started':
          setIsSyncing(true);
          setSyncProgress({ current: 0, total: 0 });
          break;
          
        case 'sync_progress':
          setSyncProgress({
            current: event.current,
            total: event.total,
            synced: event.synced,
            failed: event.failed,
          });
          break;
          
        case 'sync_complete':
          setIsSyncing(false);
          setSyncProgress(null);
          setLastSyncResult({
            synced: event.synced,
            failed: event.failed,
            remaining: event.remaining,
            timestamp: new Date(),
          });
          
          // Update pending count
          updatePendingCount();
          
          if (event.synced > 0) {
            toast.success(`${event.synced} item(s) sincronizado(s)`, {
              description: event.failed > 0 ? `${event.failed} falha(s)` : undefined,
            });
          }
          break;
          
        case 'sync_error':
          setIsSyncing(false);
          setSyncProgress(null);
          toast.error('Erro na sincronização', {
            description: event.error,
          });
          break;
          
        case 'connection_restored':
          // Auto-sync will handle this
          break;
          
        default:
          break;
      }
    });
    
    return unsubscribe;
  }, []);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    if (isDBReady) {
      const stats = await getOfflineStats();
      setPendingCount(stats.totalPending);
    }
  }, [isDBReady]);

  // Save sale offline
  const saveSaleOffline = useCallback(async (saleData) => {
    if (!isDBReady) {
      throw new Error('Offline database not ready');
    }
    
    const localId = await addPendingSale(saleData);
    await updatePendingCount();
    
    toast.info('Venda salva offline', {
      description: 'Será sincronizada quando a conexão voltar.',
    });
    
    return localId;
  }, [isDBReady, updatePendingCount]);

  // Save order offline
  const saveOrderOffline = useCallback(async (orderData) => {
    if (!isDBReady) {
      throw new Error('Offline database not ready');
    }
    
    const localId = await addPendingOrder(orderData);
    await updatePendingCount();
    
    return localId;
  }, [isDBReady, updatePendingCount]);

  // Update order offline
  const updateOrderOffline = useCallback(async (localId, updates) => {
    if (!isDBReady) {
      throw new Error('Offline database not ready');
    }
    
    return updatePendingOrder(localId, updates);
  }, [isDBReady]);

  // Get pending sales
  const getLocalPendingSales = useCallback(async () => {
    if (!isDBReady) return [];
    return getPendingSales();
  }, [isDBReady]);

  // Get pending orders
  const getLocalPendingOrders = useCallback(async () => {
    if (!isDBReady) return [];
    return getPendingOrders();
  }, [isDBReady]);

  // Cache data for offline use
  const cacheForOffline = useCallback(async (key, data) => {
    if (!isDBReady) return;
    await cacheData(key, data);
  }, [isDBReady]);

  // Get cached data
  const getFromCache = useCallback(async (key) => {
    if (!isDBReady) return null;
    return getCachedData(key);
  }, [isDBReady]);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isOnline) {
      toast.warning('Não é possível sincronizar offline');
      return null;
    }
    
    return forceSync();
  }, [isOnline]);

  const value = {
    // Connection status
    isOnline,
    isDBReady,
    
    // Sync status
    isSyncing,
    pendingCount,
    syncProgress,
    lastSyncResult,
    
    // Actions
    saveSaleOffline,
    saveOrderOffline,
    updateOrderOffline,
    getLocalPendingSales,
    getLocalPendingOrders,
    cacheForOffline,
    getFromCache,
    triggerSync,
    updatePendingCount,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
